import {
  Box,
  Flex,
  HStack,
  Icon,
  Image,
  Skeleton,
  Text,
} from '@chakra-ui/react'
import { t } from 'i18next'
import { ArrowDownLeft, ArrowUpRight } from 'lucide-react'
import { useRef, useState } from 'react'
import { Link } from 'react-router'
import ListItem from '~/components/ListItem'
import { HighlightText } from '~/components/shared/HighlightText'
import { Tooltip } from '~/components/ui/tooltip'
import {
  FaxListItemVariant,
  useFaxListItemStyle,
} from '~/pages/Fax/componentStyles/FaxListItem.style'
import { formatRelativeDay, phoneFormatter } from '~/utils/formatter'

import { formatTimeString, toNounCasing } from '@mangovoice/core'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'
import { FaxDirection, FaxInstance, FaxStatus } from '@mangovoice/types'

const IMAGE_BOX_SIZE_REMS = 5.5625
const IMAGE_BOX_SIZE_PX = IMAGE_BOX_SIZE_REMS * 16

const getStatusVariant = (status: FaxStatus) => {
  switch (status) {
    case FaxStatus.Failed:
      return FaxListItemVariant.Failed
    case FaxStatus.Pending:
      return FaxListItemVariant.Queued
    default:
      return FaxListItemVariant.Normal
  }
}

const getFaxItemStatus = (faxStatus: FaxStatus, faxDirection: FaxDirection) => {
  if (faxStatus === FaxStatus.Failed) {
    return t('faxViewList.status.failed')
  }

  if (faxDirection === FaxDirection.Outbound) {
    if (faxStatus === FaxStatus.Pending) {
      return t('faxViewList.status.pending')
    }
    return t('faxViewList.status.sent')
  }

  if (faxDirection === FaxDirection.Inbound) {
    return t('faxViewList.status.received')
  }

  return t('faxViewList.status.unknown')
}

interface FaxListViewItemProps {
  fax: FaxInstance
  isSelected: boolean
  searchTerm?: string
}

export const FaxListItem = ({
  fax,
  isSelected,
  searchTerm,
}: FaxListViewItemProps) => {
  const styles = useFaxListItemStyle({
    variant: getStatusVariant(fax.status),
  })

  const imageRef = useRef<HTMLImageElement>(undefined)
  const [scaledImageDimensions, setScaledImageDimensions] = useState<{
    height: number
    width: number
  }>()
  const isOutbound = fax.direction === FaxDirection.Outbound

  const showNumber = isOutbound
    ? fax.to_phone_number.phone_number
    : fax.from_phone_number.phone_number

  const icon = isOutbound ? ArrowUpRight : ArrowDownLeft

  const IconComponent = icon

  const faxNumber: string = phoneFormatter(showNumber)

  const statusString = getFaxItemStatus(fax.status, fax.direction)
  const isPending = fax.status === FaxStatus.Pending

  return (
    <Link data-testid="fax-list-item" to={`/faxes/${fax.id}`}>
      <ListItem
        activated={isSelected}
        aria-selected={isSelected}
        date={
          <Text css={{ ...textStyles[textStyleKeys.Body3] }}>
            {formatRelativeDay(fax.created_at)}
          </Text>
        }
        height={`${IMAGE_BOX_SIZE_REMS}rem`}
        styleType="fax"
        Thumbnail={
          // ph-no-capture: the thumbnail is the fax's first page (PHI);
          // PostHog replay records this area as an empty placeholder
          <Flex
            className="ph-no-capture"
            width={`${IMAGE_BOX_SIZE_REMS}rem`}
            css={styles.thumbnailContainer}
          >
            <Image
              src={fax.thumbnail_url}
              alt=""
              boxSize={`${IMAGE_BOX_SIZE_REMS}rem`}
              loading="lazy"
              css={styles.thumbnailImage}
              onLoad={(e) => {
                imageRef.current = e.target as HTMLImageElement
                const scaleRatios = {
                  width: IMAGE_BOX_SIZE_PX / imageRef.current.naturalWidth,
                  height: IMAGE_BOX_SIZE_PX / imageRef.current.naturalHeight,
                }

                //which is smaller, the width or the height?
                const matchScale =
                  scaleRatios.width > scaleRatios.height
                    ? scaleRatios.height
                    : scaleRatios.width

                const scaled = {
                  height: imageRef.current.naturalHeight * matchScale,
                  width: imageRef.current.naturalWidth * matchScale,
                }

                setScaledImageDimensions(scaled)
              }}
            />
            {!scaledImageDimensions && (
              <Box css={styles.skeletonContainer}>
                <Skeleton css={styles.skeleton} />
              </Box>
            )}
            {scaledImageDimensions && (
              <Box
                height={scaledImageDimensions.height}
                width={scaledImageDimensions.width}
                css={styles.thumbnailBox}
              />
            )}
          </Flex>
        }
        shouldShowUnreadDot={null}
        isUnread={!fax.read_at}
        subTitle={
          <HStack gap="0.5rem">
            <Icon css={styles.directionIcon}>
              <IconComponent size="100%" />
            </Icon>
            <Tooltip
              showArrow
              content={isPending ? t('fax.tooltip.pending') : undefined}
            >
              <Text as="span" css={styles.statusText}>
                {toNounCasing(statusString)}
              </Text>
            </Tooltip>
          </HStack>
        }
        time={
          <Text css={{ ...textStyles[textStyleKeys.Body3] }}>
            {formatTimeString(fax.created_at)}
          </Text>
        }
        title={
          <HighlightText
            css={styles.numberText}
            permutationFilters={['-', ' ', '(', ')']}
            query={searchTerm}
            color="var(--chakra-colors-fg-default)"
            fontWeight={fax.read_at ? 'normal' : 'bold'}
          >
            {faxNumber}
          </HighlightText>
        }
      />
    </Link>
  )
}
